import { useReducer, useRef, useEffect } from 'react';
import { MenuItem, Button, Container, RadioGroup, styled } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import {
  saveRequestStatus,
  roleRequireDocument,
  UploadFile,
  Justification,
  requestStatus,
  msgText,
  requestDocumentName,
  useConfirmSubmitRequest,
  getCurrentRequestType,
  getCurrentRequestRole,
  disableSaves,
  requestReducer,
  initRequestState,
  useSubmitRequest,
  useSetRequest,
  useEmploymentVerification,
  useEmploymentVerificationView,
  enableStep2,
  AddDeaNum,
  RegistrationNosEnroll,
  useDeaNumFetch,
} from '.';
import {
  FieldRequiredIndicator,
  StyledSelect,
  TooltipTrigger,
  menuItems,
  roles,
  Tooltip,
  useToggle,
  ValueLabel,
  useFeatureContext,
  useDocumentTitle,
  SuccessIcon,
  Role,
  triggerStyles,
  useRoleOpContext,
  disableOperationsHelpDesk,
  disableOperationsDiversionInv,
  variables,
} from '../common';
import { RegistrationNos } from './RegistrationNos';
import { RoleSelect } from './RoleSelect';
import { StatusSaveRequst } from './StatusSaveRequest';
import config from '../config.json';
import { justificationOptions } from './justificationOptions';
import { pathOr } from 'rambda';
import {
  flow as lflow,
  compose as lcompose,
  map as lmap,
  keys,
  isEmpty,
  filter as lfilter,
} from 'lodash/fp';

export function SaveRequest() {
  const SubmitActionHelp = styled(TooltipTrigger)(({ theme }) => ({
    marginLeft: '0px',
  }));

  useDocumentTitle({ title: 'Create New Request' });

  const { feature } = useFeatureContext();

  const [requestState, dispatchRequest] = useReducer(
    requestReducer(feature),
    initRequestState
  );

  const disableSetRequest = (a: boolean) =>
    dispatchRequest({ type: 'DisableSetRequest', payload: a });

  const { submitRequestFlow } = useEmploymentVerification({
    dispatchRequest,
  });
  useEmploymentVerificationView({ requestState });

  useDeaNumFetch({ requestState, dispatchRequest });

  const { handleSubmit, refreshToken } = useSubmitRequest({
    requestState,
    dispatchRequest,
  });

  useConfirmSubmitRequest({ requestState, refreshToken, dispatchRequest });

  useSetRequest({ requestState, dispatchRequest });
  const resetAferCancel = () =>
    dispatchRequest({
      type: 'SetRequest',
      payload: {
        ...initRequestState.request,
        requestId: requestState.request.requestId,
        role: requestState.request.role,
      },
    });

  const type = getCurrentRequestType(requestState);

  const role = getCurrentRequestRole(requestState);

  const {
    disableSave,
    disableCancel,
    disableSubmit,
    disableRevokeSave,
    disableRevokeSubmit,
    disableRenewSave,
    disableRenewSubmit,
  } = disableSaves(feature)(requestState);

  const {
    value: tooltipValue,
    toggle: toggleTooltip,
    off: offTooltip,
  } = useToggle({
    init: lcompose(
      lmap<string, ValueLabel<boolean>>(label => ({ label, value: false })),
      keys
    )(config),
  });

  const { operations } = useRoleOpContext();
  const disableOperationsHelpDsk = disableOperationsHelpDesk(operations);
  const disableOperationsDi = disableOperationsDiversionInv(operations);

  const deaNosRef = useRef<HTMLElement>(null);

  const isSuccess = (feature.sailpoint &&
    requestState.request.status === 'SUBMITTED' &&
    saveRequestStatus(requestState) === true);

  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => {
        window.location.reload();
      }, 700);

      return () => clearTimeout(timer);
    };
  }, [isSuccess])

  return isSuccess ? (
    <span>
      <SuccessIcon /> {config.requestSubmitSuccess}
    </span>
  ) : (
    <Container maxWidth="xl">
      <Wrapper>
        {/* <Card> */}
        <Actions
          style={{
            border: variables.cardBorder,
            borderRadius: '5px',
            padding: '1.5rem',
          }}>
          <legend className="info text-1-dot-2">
            Step 1 - Select Request Type
          </legend>
          <ActionLabel id="request-type">
            Request Type <span className="danger text-1-dot-2">*</span> :
          </ActionLabel>

          <Tooltip name="requestType" tooltipValue={tooltipValue}>
            <StyledSelect
              labelId="request-type"
              value={type}
              onChange={e =>
                dispatchRequest({
                  type: 'SetType',
                  payload: e.target.value as string,
                })
              }
              variant="outlined">
              <MenuItem value="ENROLL">Enroll</MenuItem>
              <MenuItem value="RENEWAL">Renew</MenuItem>
              {feature.revoke && <MenuItem value="REVOKE">Revoke</MenuItem>}
            </StyledSelect>
          </Tooltip>
          <TooltipTrigger
            name="requestType"
            toggleTooltip={toggleTooltip}
            offTooltip={offTooltip}
          />

          {type === 'ENROLL' && (
            <>
              <RoleLabel id="role">
                Role <span className="danger text-1-dot-2">*</span> :
              </RoleLabel>
              <Tooltip name="requestRole" tooltipValue={tooltipValue}>
                <RoleSelec
                  labelId="role"
                  value={role}
                  onChange={e =>
                    dispatchRequest({
                      type: 'SetRole',
                      payload: e.target.value as string,
                    })
                  }
                  variant="outlined">
                  {lcompose(menuItems, lfilter<Role>({ level: 0 }))(roles)}
                </RoleSelec>
              </Tooltip>

              <RoleTooltipTrigger
                name="requestRole"
                toggleTooltip={toggleTooltip}
                offTooltip={offTooltip}
              />
            </>
          )}

          <Justification
            value={requestState.request.comments || ''}
            options={justificationOptions[type as 'ENROLL' | 'REVOKE']}
            onChange={(payload: string) =>
              dispatchRequest({ type: 'SetJustification', payload })
            }
            otherComments={requestState.request.otherComments}
            onChangeOtherComments={(payload: string) =>
              dispatchRequest({ type: 'SetOtherComments', payload })
            }
            tooltipValue={tooltipValue}
            toggleTooltip={toggleTooltip}
            offTooltip={offTooltip}
          />

          {feature.disableDocUpload === false && roleRequireDocument(role) && (
            <UploadPOALetter>
              <UploadFile
                title="Upload Power of Attorney Letter ..."
                file={requestState.file}
                fileName={requestDocumentName(requestState)}
                onImport={lcompose(dispatchRequest, (file: File) => ({
                  type: 'SetFile',
                  payload: file,
                }))}
                className="file"
              />
            </UploadPOALetter>
          )}
        </Actions>
        {/* </Card> */}

        {enableStep2(requestState) && (
          <fieldset
            style={{
              border: variables.cardBorder,
              borderRadius: '5px',
              padding: '1.5rem',
            }}>
            <legend className="info text-1-dot-2">
              Step 2 -{' '}
              {type === 'ENROLL'
                ? 'Add DEA Registration Number'
                : 'Select DEA Registration Number'}
            </legend>
            {type === 'ENROLL' ? (
              <AddDeaNum
                deaNos={requestState.request.deaInfos}
                dispatchRequest={dispatchRequest}
                tooltipValue={tooltipValue}
                toggleTooltip={toggleTooltip}
                offTooltip={offTooltip}
                feedback={requestState.feedback}
                onSave={() => {
                  deaNosRef.current &&
                    deaNosRef.current.scrollIntoView({ behavior: 'smooth' });
                }}
              />
            ) : (
              <RegistrationNos
                deaNos={requestState.request.deaInfos}
                reqType={requestState.request.reqType}
                dispatchRequest={dispatchRequest}
                tooltipValue={tooltipValue}
                toggleTooltip={toggleTooltip}
                offTooltip={offTooltip}
                feedback={requestState.feedback}
              />
            )}
          </fieldset>
        )}

        {type === 'REVOKE' && isEmpty(requestState.request.deaInfos) && (
          <span>You do not have any active DEA registrations.</span>
        )}

        {type === 'ENROLL' && enableStep2(requestState) && (
          <fieldset
            style={{
              border: variables.cardBorder,
              borderRadius: '5px',
              padding: '1.5rem',
            }}>
            <legend className="info text-1-dot-2">
              Step 3 - Review DEA Registration Numbers
            </legend>

            <article ref={deaNosRef} style={{ marginTop: '-1rem' }}></article>

            <RegistrationNosEnroll
              deaNos={requestState.request.deaInfos}
              dispatchRequest={dispatchRequest}
              tooltipValue={tooltipValue}
              toggleTooltip={toggleTooltip}
              offTooltip={offTooltip}
              feedback={requestState.feedback}
            />
          </fieldset>
        )}

        {enableStep2(requestState) && (
          <fieldset
            style={{
              border: variables.cardBorder,
              borderRadius: '5px',
              padding: '1.5rem',
            }}>
            <legend className="info text-1-dot-2">
              {type === 'ENROLL'
                ? 'Step 4 - Validate, Save, and Submit'
                : 'Step 3 - Validate, Save, and Submit'}
            </legend>
            <StatusSaveRequst
              reqType={type}
              apiCallStatus={saveRequestStatus(requestState)}
              requestStatus={requestStatus(requestState)}
              msgText={msgText(requestState)}
              error={requestState.error}
              deaNos={requestState.request.deaInfos}
            />
            <SubmitActions>
              <Tooltip name="requestSave" tooltipValue={tooltipValue}>
                <Button
                  onClick={lflow(
                    () => disableSetRequest(true),
                    handleSubmit('NEW')
                  )}
                  disabled={
                    requestState.request.reqType === 'REVOKE'
                      ? disableRevokeSave()
                      : requestState.request.reqType === 'RENEWAL'
                        ? disableRenewSave()
                        : disableSave()
                  }
                  variant="contained"
                  color="primary">
                  {saveRequestStatus(requestState) === null &&
                    requestStatus(requestState) === 'NEW' ? (
                    <FontAwesomeIcon
                      icon={faSpinner}
                      size="1x"
                      fixedWidth
                      spin
                    />
                  ) : (
                    `validate and save`
                  )}
                </Button>
              </Tooltip>

              <SubmitActionHelp
                name="requestSave"
                toggleTooltip={toggleTooltip}
                offTooltip={offTooltip}
              />

              <Tooltip name="requestSubmit" tooltipValue={tooltipValue}>
                <Button
                  // onClick={handleSubmit('SUBMITTED')}
                  onClick={lflow(
                    () => disableSetRequest(true),
                    handleSubmit('SUBMITTED')
                  )}
                  disabled={
                    (requestState.request.reqType === 'REVOKE'
                      ? disableRevokeSubmit()
                      : requestState.request.reqType === 'RENEWAL'
                        ? disableRenewSubmit()
                        : disableSubmit()) || disableOperationsHelpDsk || disableOperationsDi
                  }
                  variant="contained"
                  color="primary">
                  {saveRequestStatus(requestState) === null &&
                    (requestStatus(requestState) === 'SUBMITTED' ||
                      submitRequestFlow) ? (
                    <FontAwesomeIcon
                      icon={faSpinner}
                      size="1x"
                      fixedWidth
                      spin
                    />
                  ) : (
                    `submit`
                  )}
                </Button>
              </Tooltip>

              <SubmitActionHelp
                name="requestSubmit"
                toggleTooltip={toggleTooltip}
                offTooltip={offTooltip}
              />

              <Tooltip name="requestCancel" tooltipValue={tooltipValue}>
                <Button
                  onClick={lflow(
                    resetAferCancel,
                    () => disableSetRequest(true),
                    handleSubmit('CANCEL')
                  )}
                  disabled={disableCancel()}
                  variant="contained"
                  color="primary">
                  {saveRequestStatus(requestState) === null &&
                    requestStatus(requestState) === 'CANCEL' ? (
                    <FontAwesomeIcon
                      icon={faSpinner}
                      size="1x"
                      fixedWidth
                      spin
                    />
                  ) : (
                    `cancel`
                  )}
                </Button>
              </Tooltip>

              <SubmitActionHelp
                name="requestCancel"
                toggleTooltip={toggleTooltip}
                offTooltip={offTooltip}
              />
            </SubmitActions>
          </fieldset>
        )}

        <article className="danger">
          <FieldRequiredIndicator /> fields are required.
        </article>
        <div role="status" aria-live="polite" className="vh">
          {requestState.feedback}
        </div>
        {/* <pre>{JSON.stringify(requestState.request, null, 2)}</pre> */}
      </Wrapper>
    </Container>
  );
}

const Wrapper = styled('article')(({ theme }) => ({
  '&>*+*': {
    marginTop: '3rem',
  },
}));
const Actions = styled('fieldset')(({ theme }) => ({
  display: 'grid',
  gap: '0.5rem',
  alignItems: 'center',
  gridAutoRows: 'minmax(1rem,auto)',

  '@media (min-width: 33.375em)': {
    gridTemplateColumns: '150px 100px 28px 100px 28px 1fr',
  },
}));
const ActionLabel = styled('label')(({ theme }) => ({
  '@media (min-width: 33.375em)': {
    justifySelf: 'end',
  },
}));
const RoleLabel = styled('label')(({ theme }) => ({
  gridRow: 4,

  '@media (min-width: 33.375em)': {
    justifySelf: 'end',

    gridRow: 3,
  },
}));
const SecondRoleLabel = styled('span')(({ theme }) => ({
  '@media (min-width: 33.375em)': {
    justifySelf: 'end',

    gridRow: 4,
    gridColumn: '1/ span 4',
  },
}));
const RoleSelec = styled(RoleSelect)(({ theme }) => ({
  gridRow: 5,

  '@media (min-width: 33.375em)': {
    gridRow: 3,
    gridColumn: '2/5',
  },
}));

const SecondRoleSelect = styled(RadioGroup)(({ theme }) => ({
  '@media (min-width: 33.375em)': {
    gridRow: 4,
    gridColumn: '5/8',
  },
}));
const UploadPOALetter = styled('span')(({ theme }) => ({
  gridRow: 7,

  '@media (min-width: 33.375em)': {
    gridRow: 2,
    gridColumnStart: 5,
    justifySelf: 'center',
  },
}));

const SubmitActions = styled('article')(({ theme }) => ({
  '& > * + *': {
    marginLeft: '1rem !important',
    marginTop: '1rem',
  },
  '& > * + .MuiButton-root': {
    marginLeft: '2rem !important',
  },
}));

export const RoleTooltipTrigger = ({
  name,
  toggleTooltip,
  offTooltip,
}: {
  name: string;
  toggleTooltip: (tooltip: string) => void;
  offTooltip: (tooltip: string) => void;
}) => (
  <TWrapper
    onClick={() => toggleTooltip(name)}
    onBlur={() => offTooltip(name)}
    aria-label={pathOr('idk', name)(config)}
    type="button">
    <FontAwesomeIcon icon={faInfoCircle} size="1x" fixedWidth />
  </TWrapper>
);

const TWrapper = styled('button')(({ theme }) => ({
  ...triggerStyles,

  '@media (min-width: 33.375em)': {
    gridRow: 3,
    gridColumn: '5',
  },
}));
